import { frozenMembershipVisitDurationMinutes } from '@/components/organisms/memberships/membership-visit-duration';
import { useCreateBookingFlow } from '@/hooks/scheduler/useCreateBookingFlow';
import { schedulingPrefix } from '@/lib/api/endpoints';
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { AlertCircle } from 'lucide-react';

import { Skeleton } from '@/components/atoms/skeleton';
import { FormPageLayout } from '@/components/layouts';
import {
  VisitRequestForm,
  type VisitRequestFormCustomer,
  type VisitRequestFormValues,
} from '@/components/organisms/scheduler/VisitRequestForm';
import type { VisitRequestFormAddress } from '@/components/organisms/scheduler/visitAddressModel';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useContact } from '@/hooks/contacts/useContact';
import { useCreateJobAppointment } from '@/hooks/jobs/useJobAppointments';
import { apiGet, operationsClient } from '@/lib/api';
import { jobEndpoints } from '@/lib/api/endpoints';
import {
  addressFieldsFromServiceAddress,
  buildCreateAllDayAppointmentDto,
  buildCreateDayFlexibleAppointmentDto,
  buildCreateAppointmentDto,
  buildCreateUnscheduledAppointmentDto,
} from '@/lib/jobs/appointmentFormModel';
import {
  formatContactAddress,
  getContactDisplayName,
  getPrimaryContactAddress,
} from '@/lib/contacts';
import { normalizeJob } from '@/lib/normalizeJob';
import { jobKeys } from '@/lib/queryKeys';
import { useHasAnyPermission } from '@/hooks/usePermissions';
import type { Job } from '@/types/job';
import type { ServiceType } from '@/types/scheduler';
import { JobPartsScheduleNotice } from '@/features/purchasing/JobPartsScheduleNotice';

export const Route = createFileRoute('/_dashboard/dashboard/jobs/$id/appointments/new')({
  component: NewAppointmentPage,
});

function NewAppointmentPage() {
  const { id } = Route.useParams();
  const router = useRouter();
  const queryClient = useQueryClient();
  const schedulingV2 = schedulingPrefix() === '/v2';
  const bookingFlow = useCreateBookingFlow({ jobId: id });
  const createAppointment = useCreateJobAppointment(id);
  const canCreateVisit = useHasAnyPermission('jobs:edit', 'jobs:create_visit');
  // Why this visit goes ahead before the job's parts are here (PUR-INV-31).
  const [partsReason, setPartsReason] = useState('');
  const parts = { partsOverrideReason: partsReason.trim() || undefined };

  const jobQuery = useApiQuery<Job>({
    queryKey: jobKeys.detail(id),
    queryFn: async () => normalizeJob(await apiGet(operationsClient, jobEndpoints.detail(id))),
    enabled: Boolean(id),
  });
  const job = jobQuery.data;
  const contactQuery = useContact(job?.contactId ?? '', { enabled: Boolean(job?.contactId) });

  const onBack = () =>
    void router.navigate({
      to: '/dashboard/jobs/$id',
      params: { id },
      search: { tab: 'visits' },
    });

  const initialCustomer = useMemo<VisitRequestFormCustomer | null>(() => {
    const contact = contactQuery.data;
    if (contact) {
      const primaryAddress = getPrimaryContactAddress(contact.addresses);
      return {
        contactId: contact.id,
        name: getContactDisplayName(contact),
        phone: contact.primaryPhone ?? '',
        email: contact.primaryEmail ?? '',
        address: formatContactAddress(primaryAddress),
      };
    }
    if (job?.contactId) {
      return {
        contactId: job.contactId,
        name: 'Job customer',
      };
    }
    return null;
  }, [contactQuery.data, job?.contactId]);
  const initialAddress = useMemo(
    () => addressFieldsFromServiceAddress(job?.serviceAddress),
    [job?.serviceAddress],
  );
  // The customer is locked to the job's contact here, so the form can't load
  // their saved addresses itself — hand them over so the visit can be sent to
  // any of them, and so a job with no service address still starts on one.
  const contactAddresses = useMemo<VisitRequestFormAddress[] | undefined>(
    () => contactQuery.data?.addresses,
    [contactQuery.data],
  );
  const allowedServiceTypes = useMemo<ServiceType[] | undefined>(() => {
    if (!job?.jobType.id) return undefined;

    return (job.jobType.requiredSkills ?? []).map((skill) => ({
      id: skill.id,
      name: skill.name,
      slug: skill.slug,
      tradeId: skill.trade?.id ?? null,
      trade: (skill.trade ?? null) as ServiceType['trade'],
      isActive: true,
    }));
  }, [job?.jobType]);
  const jobTypeContext = useMemo(() => {
    if (!job?.jobType.id) return undefined;
    return {
      id: job.jobType.id,
      name: job.jobType.name,
      minCrewSize: job.jobType.minCrewSize,
      defaultDuration:
        frozenMembershipVisitDurationMinutes(job.membershipContext) ?? job.jobType.defaultDuration,
    };
  }, [job?.jobType, job?.membershipContext]);

  async function handleSubmit(values: VisitRequestFormValues) {
    if (schedulingV2 && values.granularity !== 'ALL_DAY') {
      const result = await bookingFlow.submit(values, undefined, parts);
      await queryClient.invalidateQueries({ queryKey: jobKeys.appointments(id) });
      await queryClient.invalidateQueries({ queryKey: jobKeys.detail(id) });
      onBack();
      return;
    }

    // Scheduling v2 — day-bound shapes: no slot, no client startAt/endAt.
    // DAY_FLEXIBLE keeps time pending; ALL_DAY derives shift times server-side.
    if (values.granularity === 'ALL_DAY' || values.granularity === 'DAY_FLEXIBLE') {
      if (!values.selectedDay) {
        throw new Error('Select an available day first.');
      }
      await createAppointment.mutateAsync({
        ...parts,
        ...(values.granularity === 'DAY_FLEXIBLE'
          ? buildCreateDayFlexibleAppointmentDto({
              serviceType: values.serviceType,
              scheduledDate: values.selectedDay.date,
              technicianId: values.selectedDay.technicianId,
              durationMinutes: values.durationMinutes ?? undefined,
              details: values.details,
              serviceAddress: values.address,
              serviceAddressId: values.serviceAddressId,
              timezone: values.daysTimezone ?? undefined,
            })
          : buildCreateAllDayAppointmentDto({
              urgency: values.urgency,
              constraints: values.constraints,
              customerRequiredTechnicianId: values.customerRequiredTechnicianId,
              customerPreferredTechnicianId: values.customerPreferredTechnicianId,
              serviceType: values.serviceType,
              scheduledDate: values.selectedDay.date,
              technicianId: values.selectedDay.technicianId,
              crew: values.crew,
              crewId: values.savedCrewId,
              consecutiveDays: values.consecutiveDays,
              details: values.details,
              serviceAddress: values.address,
              serviceAddressId: values.serviceAddressId,
              timezone: values.daysTimezone ?? undefined,
            })),
      });
      await queryClient.invalidateQueries({ queryKey: jobKeys.appointments(id) });
      await queryClient.invalidateQueries({ queryKey: jobKeys.detail(id) });
      onBack();
      return;
    }
    if (!values.selectedSlot) {
      await createAppointment.mutateAsync({
        ...parts,
        ...buildCreateUnscheduledAppointmentDto({
          serviceType: values.serviceType,
          details: values.details,
          serviceAddress: values.address,
          serviceAddressId: values.serviceAddressId,
        }),
      });
      await queryClient.invalidateQueries({ queryKey: jobKeys.appointments(id) });
      await queryClient.invalidateQueries({ queryKey: jobKeys.detail(id) });
      onBack();
      return;
    }

    const appointment = await createAppointment.mutateAsync({
      ...parts,
      ...buildCreateAppointmentDto({
        serviceType: values.serviceType,
        slot: values.selectedSlot,
        preferredTechnicianId: values.technicianId,
        crew: values.crew,
        details: values.details,
        serviceAddress: values.address,
        serviceAddressId: values.serviceAddressId,
        overrideConflicts: values.overrideConflicts,
      }),
    });
    await queryClient.invalidateQueries({ queryKey: jobKeys.appointments(id) });
    await queryClient.invalidateQueries({ queryKey: jobKeys.detail(id) });
    await queryClient.invalidateQueries({ queryKey: jobKeys.appointment(id, appointment.id) });
    onBack();
  }

  if (jobQuery.isLoading) {
    return (
      <FormPageLayout title="New Visit" onBack={onBack} actions={null}>
        <div className="mx-auto max-w-3xl space-y-4 px-4 py-4">
          <Skeleton className="h-12 w-full rounded-xl" />
          <Skeleton className="h-96 w-full rounded-xl" />
        </div>
      </FormPageLayout>
    );
  }

  if (jobQuery.isError || !job) {
    return (
      <FormPageLayout title="New Visit" onBack={onBack} actions={null}>
        <div className="mx-auto max-w-3xl px-4 py-4">
          <div className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            <AlertCircle className="mt-0.5 size-4 shrink-0" />
            <span>Job not found.</span>
          </div>
        </div>
      </FormPageLayout>
    );
  }

  if (!canCreateVisit) {
    return (
      <FormPageLayout title="New Visit" onBack={onBack} actions={null}>
        <div className="mx-auto max-w-3xl px-4 py-4">
          <div className="flex items-start gap-2 rounded-lg border p-4 text-sm text-muted-foreground">
            <AlertCircle className="mt-0.5 size-4 shrink-0" />
            <span>You do not have permission to create visits for this job.</span>
          </div>
        </div>
      </FormPageLayout>
    );
  }

  const lifecycleStatus = job.statusPhase ?? job.status;
  if (lifecycleStatus === 'CANCELLED' || lifecycleStatus === 'CLOSED') {
    return (
      <FormPageLayout title="New Visit" onBack={onBack} actions={null}>
        <div className="mx-auto max-w-3xl px-4 py-4">
          <div className="flex items-start gap-2 rounded-lg border p-4 text-sm text-muted-foreground">
            <AlertCircle className="mt-0.5 size-4 shrink-0" />
            <span>Visits cannot be added to cancelled or closed jobs.</span>
          </div>
        </div>
      </FormPageLayout>
    );
  }

  return (
    <VisitRequestForm
      title="New Visit"
      onCancel={onBack}
      onSubmit={handleSubmit}
      customerMode="readonly"
      serviceSelectionMode="inheritedJobType"
      initialCustomer={initialCustomer}
      initialAddress={initialAddress}
      contactAddresses={contactAddresses}
      initialServiceAddressId={job.serviceAddress?.addressId ?? null}
      allowUnassignedManual={schedulingV2}
      allowUnscheduled
      dayJobId={job.id}
      businessUnitId={job.businessUnitId ?? undefined}
      allowDayGranularity
      submitLabel="Create"
      afterDetails={
        <JobPartsScheduleNotice
          jobId={job.id}
          reason={partsReason}
          onReasonChange={setPartsReason}
        />
      }
      allowedServiceTypes={allowedServiceTypes}
      jobTypeContext={jobTypeContext}
      context={{
        label: 'Job',
        title: job.name,
        subtitle: job.jobNumber,
      }}
    />
  );
}
